import ReactDOM from 'react-dom';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from "../../services/api";
import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer";
import { Modal, Spinner, Table, Button, Form, Badge } from 'react-bootstrap';
import Swal from "sweetalert2";
import {
    FaBoxes,
    FaPlay,
    FaSyncAlt,
    FaExclamationTriangle,
    FaListAlt,
    FaPrint,
    FaSearch,
    FaBan,
    FaCheckCircle,
    FaEdit,
    FaFileDownload,
    FaFilter
} from "react-icons/fa";
import RelatorioCriticaPrint from './RelatorioCriticaPrint';

export default function DashboardInventario() {
    const [loja, setLoja] = useState('1');
    const [lojasLista, setLojasLista] = useState([]);
    const [tipoFiltro, setTipoFiltro] = useState('TODOS');
    const [inventarios, setInventarios] = useState([]);
    const [secoesCatalogo, setSecoesCatalogo] = useState([]);
    const [selectedInv, setSelectedInv] = useState(null);
    const [lotes, setLotes] = useState([]);
    const [critica, setCritica] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filtros Avançados do Card Principal
    const [dtInicio, setDtInicio] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 10);
        return d.toISOString().split('T')[0];
    });

    const [dtFim, setDtFim] = useState(() => {
        return new Date().toISOString().split('T')[0];
    });

    const [statusTemp, setStatusTemp] = useState({
        A: true,
        E: true,
        C: true,
        FERP: false
    });

    const [statusAplicados, setStatusAplicados] = useState({
        A: true,
        E: true,
        C: true,
        FERP: false
    });

    // Filtros de Crítica & Popover Status
    const [valorCorte, setValorCorte] = useState('0,00');
    const [buscaProduto, setBuscaProduto] = useState('');
    const [ordemCritica, setOrdemCritica] = useState('VALOR_DESC');
    const [showDropdownStatus, setShowDropdownStatus] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

    // Modais
    const [showModalLotes, setShowModalLotes] = useState(false);
    const [showModalCritica, setShowModalCritica] = useState(false);
    const [showModalSecoes, setShowModalSecoes] = useState(false);
    const [showModalRelLote, setShowModalRelLote] = useState(false);

    // Modal Especial "SEM ESTOQUE" e "ITENS"
    const [showModalSemEstoque, setShowModalSemEstoque] = useState(false);
    const [produtosSemEstoque, setProdutosSemEstoque] = useState([]);
    const [exibirEstoque, setExibirEstoque] = useState(false);
    const [secaoFiltroSelect, setSecaoFiltroSelect] = useState('TODAS');

    // Modal Recontagem Lote 9999
    const [showModalPosCritica, setShowModalPosCritica] = useState(false);
    const [itemPosCritica, setItemPosCritica] = useState(null);
    const [novaQtdFinal, setNovaQtdFinal] = useState('');

    const [relatorioLoteDados, setRelatorioLoteDados] = useState(null);
    const [qtdeLotesLiberar, setQtdeLotesLiberar] = useState(1);

    const formatarMoeda = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

    const extrairCodLoja = (item) => {
        if (!item) return '1';
        const val = item.cod_emp ?? item.codigo ?? item.codloja ?? item;
        return String(parseInt(val, 10) || 1);
    };

    const valorCorteNumerico = useMemo(() => {
        const rawValor = String(valorCorte).replace(/\./g, '').replace(',', '.');
        const parsed = parseFloat(rawValor);
        return isNaN(parsed) ? 0 : parsed;
    }, [valorCorte]);

    const resumoLotes = useMemo(() => {
        const lotesAtivos = lotes.filter(l => l.inv_status !== 'I');
        const liberados = lotesAtivos.filter(l => l.inv_status === 'L').length;
        const coletados = lotesAtivos.filter(l => l.inv_status === 'F').length;
        const total = lotesAtivos.length;
        const prontoParaDivergencia = total > 0 && liberados === 0 && coletados === total;

        return { total, liberados, coletados, prontoParaDivergencia };
    }, [lotes]);

    const criticaAgrupadaPorSecao = useMemo(() => {
        const grupos = {};

        critica.forEach(item => {
            const secaoKey = item.desc_secao || 'OUTROS';
            if (!grupos[secaoKey]) {
                grupos[secaoKey] = {
                    cod_secao: item.cod_secao || '',
                    desc_secao: secaoKey,
                    itens: []
                };
            }
            grupos[secaoKey].itens.push(item);
        });

        Object.keys(grupos).forEach(key => {
            grupos[key].itens.sort((a, b) => {
                switch (ordemCritica) {
                    case 'VALOR_DESC':
                        return (b.divergencia_valor || 0) - (a.divergencia_valor || 0);
                    case 'VALOR_ASC':
                        return (a.divergencia_valor || 0) - (b.divergencia_valor || 0);
                    case 'CODIGO':
                        return String(a.codigo).localeCompare(String(b.codigo));
                    case 'DESCRICAO':
                        return String(a.descricao).localeCompare(String(b.descricao));
                    default:
                        return (b.divergencia_valor || 0) - (a.divergencia_valor || 0);
                }
            });
        });

        return Object.values(grupos);
    }, [critica, ordemCritica]);

    const buscarInventariosPorLoja = useCallback(async (codLojaAlvo, statusParaFiltrar = statusAplicados) => {
        const codLimpo = String(parseInt(codLojaAlvo, 10) || 1);
        const statusArray = Object.keys(statusParaFiltrar).filter(k => statusParaFiltrar[k]);

        if (statusArray.length === 0) {
            setInventarios([]);
            return;
        }

        setLoading(true);

        const statusParam = statusArray.length === 4
            ? 'TODOS'
            : statusArray.map(s => `'${s}'`).join(',');

        try {
            const res = await api.get(`/inventario/consinco/${codLimpo}`, {
                params: {
                    dt_inicio: dtInicio,
                    dt_fim: dtFim,
                    status_list: statusParam
                }
            });
            setInventarios(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Erro ao carregar inventários:", err);
            setInventarios([]);
            Swal.fire("Atenção", `Nenhum inventário encontrado na Loja ${codLimpo}.`, "warning");
        } finally {
            setLoading(false);
        }
    }, [dtInicio, dtFim, statusAplicados]);

    useEffect(() => {
        let isMounted = true;

        const inicializar = async () => {
            try {
                const resLojas = await api.get('/usuario/loja');
                if (isMounted && Array.isArray(resLojas.data) && resLojas.data.length > 0) {
                    setLojasLista(resLojas.data);
                    const primeiraLoja = extrairCodLoja(resLojas.data[0]);
                    setLoja(primeiraLoja);
                    buscarInventariosPorLoja(primeiraLoja, statusAplicados);
                } else {
                    throw new Error("Lojas indisponíveis");
                }
            } catch (err) {
                if (isMounted) {
                    const fallbackLojas = [
                        { cod_emp: 1, razao: 'Loja 01 - Matriz' },
                        { cod_emp: 2, razao: 'Loja 02' },
                        { cod_emp: 3, razao: 'Loja 03' },
                        { cod_emp: 4, razao: 'Loja 04' }
                    ];
                    setLojasLista(fallbackLojas);
                    setLoja('1');
                    buscarInventariosPorLoja('1', statusAplicados);
                }
            }

            try {
                const resSecoes = await api.get('/inventario/secoes');
                if (isMounted) {
                    setSecoesCatalogo(Array.isArray(resSecoes.data) ? resSecoes.data : []);
                }
            } catch (err) {
                console.error("Erro ao carregar catálogo de seções:", err);
            }
        };

        inicializar();

        return () => { isMounted = false; };
    }, []);

    const handleLojaChange = (e) => {
        const novaLoja = extrairCodLoja(e.target.value);
        setLoja(novaLoja);
        buscarInventariosPorLoja(novaLoja, statusAplicados);
    };

    const handleStatusCheckboxChange = (statusKey) => {
        setStatusTemp(prev => ({
            ...prev,
            [statusKey]: !prev[statusKey]
        }));
    };

    const inventariosFiltrados = useMemo(() => {
        if (tipoFiltro === 'TODOS') return inventarios;
        return inventarios.filter(inv => String(inv.tipo).toUpperCase() === tipoFiltro);
    }, [inventarios, tipoFiltro]);

    const handleAbrirTipoModal = async (inv) => {
        setSelectedInv(inv);
        const tipoUpper = String(inv.tipo).toUpperCase();

        if (tipoUpper === 'PARCIAL') {
            setShowModalSecoes(true);
        } else if (tipoUpper === 'SEM ESTOQUE' || tipoUpper === 'ITENS') {
            setLoading(true);
            try {
                const res = await api.get(`/inventario/produtos-relatorio/${loja}/${inv.numinv}`);
                setProdutosSemEstoque(Array.isArray(res.data) ? res.data : []);
                setSecaoFiltroSelect('TODAS');
                setShowModalSemEstoque(true);
            } catch (err) {
                Swal.fire("Erro", "Falha ao carregar itens do inventário", "error");
            } finally {
                setLoading(false);
            }
        }
    };

    const secoesUnicasSemEstoque = useMemo(() => {
        const grupos = new Set();
        produtosSemEstoque.forEach(p => {
            if (p.desc_secao) grupos.add(p.desc_secao);
        });
        return Array.from(grupos);
    }, [produtosSemEstoque]);

    const produtosAgrupadosPorSecao = useMemo(() => {
        const filtrados = secaoFiltroSelect === 'TODAS'
            ? produtosSemEstoque
            : produtosSemEstoque.filter(p => String(p.desc_secao) === String(secaoFiltroSelect));

        const grupos = {};
        filtrados.forEach(item => {
            const secKey = item.desc_secao || 'OUTROS';
            if (!grupos[secKey]) {
                grupos[secKey] = {
                    cod_secao: item.cod_secao || '',
                    desc_secao: secKey,
                    itens: []
                };
            }
            grupos[secKey].itens.push(item);
        });

        return Object.values(grupos);
    }, [produtosSemEstoque, secaoFiltroSelect]);

    const imprimirRelatorioSemEstoque = () => {
        const win = window.open('', '_blank');
        if (!win) {
            Swal.fire("Atenção", "Permita os pop-ups no navegador para imprimir o relatório.", "warning");
            return;
        }

        const logoUrl = `${window.location.origin}/Images/arbor.png`;

        const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <title>Relação de Produtos - Inventário ${selectedInv?.numinv}</title>
    <style>
        @page { size: A4 portrait; margin: 8mm 10mm; }
        body { font-family: Arial, sans-serif; font-size: 8pt; margin: 0; padding: 0; color: #000; }
        .brand-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #991b1b; padding-bottom: 6px; margin-bottom: 6px; }
        .brand-logo-img { max-height: 50px; width: auto; object-fit: contain; }
        .titulo { font-size: 13pt; font-weight: bold; color: #991b1b; margin-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; margin-top: 5px; table-layout: fixed; }
        th { border-bottom: 2px solid #991b1b; border-top: 1px solid #000; padding: 4px; text-align: left; font-size: 8pt; }
        td { padding: 4px; border-bottom: 1px solid #ddd; vertical-align: top; font-size: 8pt; word-wrap: break-word; }
        .secao-header-row { background-color: #f8d7da; font-weight: bold; font-size: 9pt; }
        .contagem-box { width: 45px; height: 16px; border: 1px solid #000; margin: 0 auto; }
        .ean-sub { font-size: 7.5pt; color: #555; font-family: monospace; margin-top: 2px; }
    </style>
</head>
<body>
    <div class="brand-header">
        <img src="${logoUrl}" alt="ARBOR" class="brand-logo-img" />
        <div style="font-size: 9pt; font-weight: bold;">Módulo de Inventário Consinco</div>
    </div>
    <div>
        <div class="titulo">Relação de Itens para Contagem (${selectedInv?.tipo || 'SEM ESTOQUE'})</div>
        <div>
            <strong>Inventário:</strong> ${selectedInv?.numinv} &nbsp;&nbsp;|&nbsp;&nbsp;
            <strong>Descrição:</strong> ${selectedInv?.descricao || ''} &nbsp;&nbsp;|&nbsp;&nbsp;
            <strong>Loja:</strong> ${loja}
        </div>
    </div>
    <table>
        <thead>
            <tr>
                <th style="width: 12%;">Código</th>
                <th style="width: ${exibirEstoque ? '58%' : '68%'};">Descrição / Múltiplos EANs</th>
                <th style="width: 10%;">Emb.</th>
                ${exibirEstoque ? '<th style="width: 10%; text-align: right;">Estoque</th>' : ''}
                <th style="width: 10%; text-align: center;">Contagem</th>
            </tr>
        </thead>
        <tbody>
            ${produtosAgrupadosPorSecao.map(grupo => `
                <tr class="secao-header-row">
                    <td colSpan="${exibirEstoque ? 5 : 4}" style="padding: 4px 6px;">
                        ${grupo.cod_secao ? `${grupo.cod_secao} - ` : ''}${grupo.desc_secao}
                    </td>
                </tr>
                ${grupo.itens.map(p => `
                    <tr>
                        <td><strong>${p.prod || p.codigo}</strong></td>
                        <td>
                            <div>${p.descricao}</div>
                            <div class="ean-sub">EANs: ${p.eans_adicionais || p.ean || ''}</div>
                        </td>
                        <td>${p.qtde_embalagem || 1}</td>
                        ${exibirEstoque ? `<td style="text-align: right;">${p.estoque || 0}</td>` : ''}
                        <td style="text-align: center;"><div class="contagem-box"></div></td>
                    </tr>
                `).join('')}
            `).join('')}
        </tbody>
    </table>
    <script>
        window.onload = function() {
            window.focus();
            setTimeout(function() { window.print(); }, 300);
            window.onafterprint = function() { window.close(); };
        };
    </script>
</body>
</html>`;

        win.document.write(htmlContent);
        win.document.close();
    };

    const handleGerenciarLotes = async (inv) => {
        setSelectedInv(inv);
        setLoading(true);
        try {
            const res = await api.get(`/inventario/lotes/${loja}/${inv.numinv}`);
            setLotes(Array.isArray(res.data) ? res.data : []);
            setShowModalLotes(true);
        } catch (err) {
            Swal.fire("Erro", "Falha ao carregar lotes de coleta", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleLiberarLotesEmMassa = async () => {
        if (!qtdeLotesLiberar || qtdeLotesLiberar < 1) {
            Swal.fire("Atenção", "Informe a quantidade de lotes", "warning");
            return;
        }
        try {
            await api.post('/inventario/lotes/liberar-massa', {
                inv_loja: Number(loja),
                inv_numinv: Number(selectedInv.numinv),
                qtde_lotes: Number(qtdeLotesLiberar)
            });
            Swal.fire("Sucesso", `${qtdeLotesLiberar} Lote(s) liberado(s) com sucesso!`, "success");
            setQtdeLotesLiberar(1);
            handleGerenciarLotes(selectedInv);
            buscarInventariosPorLoja(loja, statusAplicados);
        } catch (err) {
            Swal.fire("Erro", "Falha ao liberar lotes", "error");
        }
    };

    const handleSolicitarRecontagem = async (lote) => {
        const confirm = await Swal.fire({
            title: "Recontar Lote?",
            text: `Deseja solicitar recontagem para o Lote ${lote.inv_lote}?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Sim, Recontar",
            confirmButtonColor: "#991b1b"
        });

        if (confirm.isConfirmed) {
            try {
                await api.post('/inventario/lote/recontar', {
                    inv_loja: Number(loja),
                    inv_numinv: Number(selectedInv.numinv),
                    inv_lote: Number(lote.inv_lote)
                });
                Swal.fire("Sucesso", "Nova ordem de recontagem enviada!", "success");
                handleGerenciarLotes(selectedInv);
            } catch (err) {
                Swal.fire("Erro", "Falha ao solicitar recontagem", "error");
            }
        }
    };

    const handleInativarLote = async (lote) => {
        const novoStatus = lote.inv_status === 'I' ? 'L' : 'I';
        const acao = novoStatus === 'I' ? 'Inativar' : 'Ativar';

        const confirm = await Swal.fire({
            title: `${acao} Lote ${lote.inv_lote}?`,
            text: `Deseja realmente ${acao.toLowerCase()} este lote?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: `Sim, ${acao}`,
            confirmButtonColor: "#991b1b"
        });

        if (confirm.isConfirmed) {
            try {
                await api.post('/inventario/lote/inativar', {
                    inv_loja: Number(loja),
                    inv_numinv: Number(selectedInv.numinv),
                    inv_lote: Number(lote.inv_lote),
                    inv_contagem: Number(lote.inv_contagem),
                    inv_status: novoStatus
                });
                Swal.fire("Sucesso", `Lote ${acao.toLowerCase()}do com sucesso!`, "success");
                handleGerenciarLotes(selectedInv);
                buscarInventariosPorLoja(loja, statusAplicados);
            } catch (err) {
                Swal.fire("Erro", `Falha ao ${acao.toLowerCase()} lote`, "error");
            }
        }
    };

    const handleImprimirLote = async (lote) => {
        setLoading(true);
        try {
            const res = await api.get(`/inventario/lote/relatorio/${loja}/${selectedInv.numinv}/${lote.inv_lote}/${lote.inv_contagem}`);
            setRelatorioLoteDados(res.data);
            setShowModalRelLote(true);
        } catch (err) {
            Swal.fire("Erro", "Falha ao carregar relatório do lote", "error");
        } finally {
            setLoading(false);
        }
    };

    const carregarCriticaComFiltros = async (invAlvo = selectedInv, resetFiltros = false) => {
        if (!invAlvo) return;
        setLoading(true);

        if (resetFiltros) {
            setCritica([]);
            setBuscaProduto('');
            setValorCorte('0,00');
        }

        try {
            const res = await api.get(`/inventario/critica/${loja}/${invAlvo.numinv}`, {
                params: {
                    valor: resetFiltros ? 0 : valorCorteNumerico,
                    busca: resetFiltros ? '' : buscaProduto
                }
            });
            setCritica(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            Swal.fire("Erro", "Falha ao carregar itens/crítica", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleAbrirDivergencias = async (inv) => {
        setCritica([]);
        setSelectedInv(inv);
        setBuscaProduto('');
        setValorCorte('0,00');

        setLoading(true);
        try {
            if (inv.status_calculado !== 'FERP') {
                const resLotes = await api.get(`/inventario/lotes/${loja}/${inv.numinv}`);
                const listaLotes = Array.isArray(resLotes.data) ? resLotes.data : [];
                const lotesAtivos = listaLotes.filter(l => l.inv_status !== 'I');
                const pendentes = lotesAtivos.filter(l => l.inv_status !== 'F').length;

                if (lotesAtivos.length === 0) {
                    Swal.fire("Atenção", "Inventário não possui lotes cadastrados no Arbor.", "warning");
                    return;
                }

                if (pendentes > 0) {
                    Swal.fire("Atenção", "A crítica só pode ser gerada após TODOS os lotes ativos serem coletados/finalizados.", "warning");
                    return;
                }
            }

            await carregarCriticaComFiltros(inv, true);
            setShowModalCritica(true);
        } catch (err) {
            Swal.fire("Erro", "Falha ao carregar lotes do inventário.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSalvarAjustePosCritica = async () => {
        const finalNum = parseFloat(String(novaQtdFinal).replace(',', '.'));
        if (isNaN(finalNum)) {
            Swal.fire("Atenção", "Informe uma quantidade válida.", "warning");
            return;
        }

        try {
            await api.post('/inventario/conferencia/ajuste9999', {
                inv_loja: Number(loja),
                inv_numinv: Number(selectedInv.numinv),
                inv_ean: itemPosCritica.ean || itemPosCritica.codigo,
                qtd_atual: Number(itemPosCritica.qtd_contada || 0),
                qtd_final: finalNum
            });

            Swal.fire("Sucesso", "Ajuste registrado com sucesso no Lote 9999!", "success");
            setShowModalPosCritica(false);
            carregarCriticaComFiltros(selectedInv);
        } catch (err) {
            Swal.fire("Erro", "Falha ao salvar ajuste no lote 9999.", "error");
        }
    };

    const handleExportarConsinco = async (invAlvo = selectedInv) => {
        if (!invAlvo) return;
        try {
            setLoading(true);
            const response = await api.get(`/inventario/exportar-consinco/${loja}/${invAlvo.numinv}`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Inventario_${invAlvo.numinv}.txt`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            Swal.fire("Sucesso", "Arquivo de importação Consinco gerado com sucesso!", "success");
        } catch (err) {
            Swal.fire("Erro", "Falha ao gerar o arquivo do Consinco.", "error");
        } finally {
            setLoading(false);
        }
    };

    const imprimirRelatorioLote = (dadosLote) => {
        if (!dadosLote) return;

        const win = window.open('', '_blank');
        if (!win) {
            Swal.fire("Atenção", "Permita os pop-ups no navegador para imprimir o relatório.", "warning");
            return;
        }

        const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <title>Relatório do lote ${dadosLote.cabecalho?.inv_lote || ''} do inventário ${selectedInv?.numinv || ''}</title>
    <style>
        @page { size: A4 portrait; margin: 8mm 10mm 15mm 10mm; }
        html, body { font-family: Arial, sans-serif; font-size: 9pt; color: #000; margin: 0; padding: 0; background: #fff; }
        .header { border-bottom: 2px solid #991b1b; padding-bottom: 8px; margin-bottom: 12px; }
        .titulo { font-size: 16pt; font-weight: bold; text-align: center; margin: 0 0 8px 0; color: #991b1b; }
        .info-grid { display: flex; justify-content: space-between; margin-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        th { border-bottom: 2px solid #991b1b; border-top: 1px solid #000; padding: 6px 4px; text-align: center; font-weight: bold; }
        td { padding: 5px 4px; border-bottom: 1px solid #ddd; text-align: center; }
        .text-start { text-align: left; }
        .fw-bold { font-weight: bold; }

        .page-footer {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            border-top: 1px solid #000;
            padding-top: 6px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 8.5pt;
            background: #fff;
        }
    </style>
</head>
<body>
    <div class="header">
        <h1 class="titulo">Relatório de Lotes Coletados</h1>
        <div class="info-grid">
            <span><strong>Status:</strong> C - Coletado</span>
            <span><strong>Inventário:</strong> ${selectedInv?.numinv || ''}</span>
            <span><strong>Lote:</strong> ${dadosLote.cabecalho?.inv_lote || ''}</span>
            <span><strong>Contagem:</strong> ${dadosLote.cabecalho?.inv_contagem || 1}ª</span>
        </div>
        <div class="info-grid">
            <span><strong>Conferente:</strong> ${dadosLote.cabecalho?.nome_usuario_coleta || ''}</span>
            <span><strong>Data/Hora:</strong> ${dadosLote.cabecalho?.inv_data || new Date().toLocaleString('pt-BR')}</span>
        </div>
    </div>

    <table>
        <thead>
            <tr>
                <th style="width: 8%;">Seq</th>
                <th style="width: 15%;">Cód. Interno</th>
                <th style="width: 18%;">EAN</th>
                <th class="text-start">Descrição</th>
                <th style="width: 12%;">Coletado</th>
            </tr>
        </thead>
        <tbody>
            ${dadosLote.itens?.map(it => `
                <tr>
                    <td>${it.seq}</td>
                    <td class="fw-bold">${it.codigo || it.cod_produto || ''}</td>
                    <td>${it.ean || ''}</td>
                    <td class="text-start">${it.descricao || ''}</td>
                    <td class="fw-bold">${it.coletado}</td>
                </tr>
            `).join('') || ''}
        </tbody>
    </table>

    <div class="page-footer">
        <span>Loja: ${loja} | Inventário: ${selectedInv?.numinv || ''}</span>
        <span>Assinatura Conferente: _____________________________________</span>
    </div>

    <script>
        window.onload = function() {
            window.focus();
            setTimeout(function() {
                window.print();
                window.focus();
            }, 300);
            window.onafterprint = function() {
                window.close();
            };
        };
    </script>
</body>
</html>
`;

        win.document.write(htmlContent);
        win.document.close();
    };

    const imprimirRelatorioCritica = () => {
        if (!criticaAgrupadaPorSecao || criticaAgrupadaPorSecao.length === 0) {
            Swal.fire("Atenção", "Nenhum item na crítica para imprimir.", "warning");
            return;
        }

        const win = window.open('', '_blank');
        if (!win) {
            Swal.fire("Atenção", "Permita os pop-ups no navegador para imprimir o relatório.", "warning");
            return;
        }

        const logoUrl = `${window.location.origin}/Images/arbor.png`;

        const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <title>Relatório de Crítica do inventário: ${selectedInv?.numinv || ''}</title>
    <style>
        @page { size: A4 portrait; margin: 8mm 10mm 15mm 10mm; }
        html, body { margin: 0; padding: 0; background: #fff; color: #000; font-family: Arial, sans-serif; font-size: 8pt; }
        .brand-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #991b1b; padding-bottom: 6px; margin-bottom: 6px; }
        .brand-logo-img { max-height: 60px; width: auto; object-fit: contain; }
        .header-top { display: flex; justify-content: space-between; align-items: center; margin-top: 2px; }
        .relatorio-titulo { font-size: 14pt; font-weight: bold; margin: 0; color: #991b1b; }
        .header-corte-badge { border: 2px solid #991b1b; border-radius: 50%; padding: 4px 12px; font-size: 16pt; font-weight: bold; color: #991b1b; }
        .legenda-topo { font-size: 7.5pt; font-weight: bold; font-style: italic; margin-top: 2px; }
        .relatorio-table { width: 100%; border-collapse: collapse; margin-top: 6px; table-layout: fixed; }
        th { border-bottom: 1px solid #991b1b; border-top: 1px solid #000; padding: 4px 2px; text-align: left; font-weight: bold; font-size: 8pt; }
        td { padding: 3px 2px; vertical-align: middle; font-size: 8pt; word-wrap: break-word; }
        .secao-header-row { background-color: #f8d7da; font-weight: bold; font-size: 9pt; }
        .check-box { width: 22px; height: 12px; border: 1px solid #000; margin: 0 auto; }
        .cell-lotes { padding: 1px 0 3px 10px !important; font-family: 'Courier New', monospace; font-size: 7.5pt; border-bottom: 1px dashed #ccc; }
        .lotes-grid { display: flex; flex-wrap: wrap; gap: 12px; }
        .item-fora { font-weight: bold; }
        .ean-sub { font-size: 7pt; color: #555; font-family: monospace; margin-top: 1px; }

        .relatorio-footer { 
            position: fixed; 
            bottom: 0; 
            left: 0; 
            right: 0; 
            padding-top: 4px; 
            border-top: 1px solid #000; 
            display: flex; 
            justify-content: space-between; 
            align-items: flex-end; 
            font-size: 8pt;
            background: #fff;
        }
    </style>
</head>
<body>
    <div class="brand-header">
        <img src="${logoUrl}" alt="ARBOR" class="brand-logo-img" />
        <div style="font-size: 9pt; font-weight: bold;">Módulo de Inventário Consinco</div>
    </div>

    <div class="header-top">
        <div>
            <h1 class="relatorio-titulo">Crítica do Inventário</h1>
            <div style="margin-top: 2px;">
                <strong>Inventário:</strong> ${selectedInv?.numinv || ''} &nbsp;&nbsp;&nbsp;
                <strong>Descrição:</strong> ${selectedInv?.descricao || 'INV. DE ITENS'}
            </div>
            <div class="legenda-topo">*** - Produtos não pertencentes ao inventário</div>
        </div>
        <div class="header-corte-badge">${formatarMoeda(valorCorteNumerico).replace('R$', '').trim()}</div>
    </div>

    <table class="relatorio-table">
        <thead>
            <tr>
                <th style="width: 12%;">Código</th>
                <th style="width: 48%;">Descrição / EANs</th>
                <th style="width: 6%; text-align: center;">Emb.</th>
                <th style="width: 10%; text-align: right;">Custo</th>
                <th style="width: 10%; text-align: right;">Diferença</th>
                <th style="width: 8%; text-align: right;">Coletado</th>
                <th style="width: 6%; text-align: center;">Check</th>
            </tr>
        </thead>
        <tbody>
            ${criticaAgrupadaPorSecao.map(grupo => `
                <tr class="secao-header-row">
                    <td colSpan="7" style="padding: 4px 6px;">
                        ${grupo.cod_secao ? `${grupo.cod_secao} - ` : ''}${grupo.desc_secao}
                    </td>
                </tr>
                ${grupo.itens.map(prod => {
            const isFora = prod.fora_inventario === 'S';
            let lotesList = [];
            try {
                if (prod.lotes_json) {
                    lotesList = typeof prod.lotes_json === 'string' ? JSON.parse(prod.lotes_json) : prod.lotes_json;
                } else if (prod.lotes_coletados) {
                    lotesList = prod.lotes_coletados;
                }
            } catch (e) {
                lotesList = [];
            }

            const descFormatada = isFora ? `*** ${prod.descricao}` : prod.descricao;

            return `
                        <tr>
                            <td><strong>${prod.codigo}</strong></td>
                            <td class="${isFora ? 'item-fora' : ''}">
                                <div>${descFormatada}</div>
                                <div class="ean-sub">EANs: ${prod.ean || prod.codigo}</div>
                            </td>
                            <td style="text-align: center;">${prod.emb || '1'}</td>
                            <td style="text-align: right;">${formatarMoeda(prod.custo).replace('R$', '').trim()}</td>
                            <td style="text-align: right;"><strong>${formatarMoeda(prod.divergencia_valor).replace('R$', '').trim()}</strong></td>
                            <td style="text-align: right;">${prod.qtd_contada || 0}</td>
                            <td style="text-align: center;"><div class="check-box"></div></td>
                        </tr>
                        ${lotesList && lotesList.length > 0 ? `
                            <tr>
                                <td colSpan="7" class="cell-lotes">
                                    <div class="lotes-grid">
                                        ${lotesList.map(lt => `
                                            <span>Lote: <strong>${lt.inv_lote}</strong> &nbsp; Seq <strong>${lt.inv_seq}</strong> &nbsp; Qtd <u>${lt.inv_qtde}</u></span>
                                        `).join('')}
                                    </div>
                                </td>
                            </tr>
                        ` : ''}
                    `;
        }).join('')}
            `).join('')}
        </tbody>
    </table>

    <div class="relatorio-footer">
        <div>Loja: ${loja} | Inventário: ${selectedInv?.numinv || ''}</div>
        <div>Assinatura Conferente: ____________________________________</div>
    </div>

    <script>
        window.onload = function() {
            window.focus();
            setTimeout(function() {
                window.print();
                window.focus();
            }, 300);
            window.onafterprint = function() {
                window.close();
            };
        };
    </script>
</body>
</html>
`;

        win.document.write(htmlContent);
        win.document.close();
    };

    const secoesDoInventario = useMemo(() => {
        if (!selectedInv || !selectedInv.secao) return [];
        const codigos = String(selectedInv.secao).split(',').map(s => Number(s.trim()));
        return secoesCatalogo.filter(sc => sc.tp === 'SC' && codigos.includes(sc.secao));
    }, [selectedInv, secoesCatalogo]);

    return (
        <div className="bg-light min-vh-100">
            <style>{`
                .btn-custom-red {
                    background-color: #991b1b !important;
                    border-color: #991b1b !important;
                    color: #fff !important;
                }
                .btn-custom-red:hover {
                    background-color: #7f1d1d !important;
                    border-color: #7f1d1d !important;
                }
                .card-header-red {
                    background-color: #991b1b !important;
                    color: #fff !important;
                }
            `}</style>

            <Menu modulo="ARBOR" />

            <Modal show={loading} centered backdrop="static" keyboard={false} size="sm">
                <Modal.Body className="text-center p-4">
                    <Spinner animation="border" style={{ color: '#991b1b' }} className="mb-2" />
                    <div className="fw-bold text-dark">Carregando...</div>
                </Modal.Body>
            </Modal>

            <main className="container-fluid" style={{ paddingTop: '95px' }}>
                <div className="card shadow-sm border-0 mb-3" style={{ position: 'relative', zIndex: 1 }}>
                    <div className="card-body py-2">
                        <div className="row align-items-end g-2">
                            <div className="col-md-2">
                                <Form.Label className="small fw-bold mb-1">Loja</Form.Label>
                                <Form.Select
                                    style={{ height: '38px' }}
                                    value={loja}
                                    onChange={handleLojaChange}
                                >
                                    {lojasLista.map((lj, i) => {
                                        const c = extrairCodLoja(lj);
                                        return (
                                            <option key={i} value={c}>
                                                {lj.razao || lj.nome || `Loja ${c}`}
                                            </option>
                                        );
                                    })}
                                </Form.Select>
                            </div>

                            <div className="col-md-2">
                                <Form.Label className="small fw-bold mb-1">Data Início</Form.Label>
                                <Form.Control
                                    style={{ height: '38px' }}
                                    type="date"
                                    value={dtInicio}
                                    onChange={e => setDtInicio(e.target.value)}
                                />
                            </div>

                            <div className="col-md-2">
                                <Form.Label className="small fw-bold mb-1">Data Fim</Form.Label>
                                <Form.Control
                                    style={{ height: '38px' }}
                                    type="date"
                                    value={dtFim}
                                    onChange={e => setDtFim(e.target.value)}
                                />
                            </div>

                            <div className="col-md-2">
                                <Form.Label className="small fw-bold mb-1">Status</Form.Label>
                                <Button
                                    id="btn-status-dropdown"
                                    variant="outline-secondary"
                                    className="w-100 d-flex align-items-center justify-content-between bg-white text-dark border shadow-sm px-2"
                                    style={{ height: '38px' }}
                                    onClick={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setDropdownPos({
                                            top: rect.bottom,
                                            left: rect.left,
                                            width: rect.width
                                        });
                                        setStatusTemp({ ...statusAplicados });
                                        setShowDropdownStatus(!showDropdownStatus);
                                    }}
                                >
                                    <span className="small text-truncate">
                                        <FaFilter className="me-1" style={{ color: '#991b1b' }} />
                                        {Object.values(statusAplicados).filter(Boolean).length === 4
                                            ? "Todos Selecionados"
                                            : `${Object.values(statusAplicados).filter(Boolean).length} Selecionados`}
                                    </span>
                                    <span className="ms-1 small text-muted">▼</span>
                                </Button>

                                {showDropdownStatus && ReactDOM.createPortal(
                                    <div
                                        className="bg-white border rounded shadow-lg p-2"
                                        style={{
                                            position: 'fixed',
                                            top: `${dropdownPos.top + 4}px`,
                                            left: `${dropdownPos.left}px`,
                                            width: `${Math.max(dropdownPos.width, 220)}px`,
                                            zIndex: 99999
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
                                            <span className="fw-bold small text-muted">Filtrar por Status</span>
                                            <Button
                                                size="sm"
                                                variant="link"
                                                className="p-0 text-decoration-none small"
                                                onClick={() => {
                                                    const todosMarcados = Object.values(statusTemp).every(Boolean);
                                                    setStatusTemp({
                                                        A: !todosMarcados,
                                                        E: !todosMarcados,
                                                        C: !todosMarcados,
                                                        FERP: !todosMarcados
                                                    });
                                                }}
                                            >
                                                {Object.values(statusTemp).every(Boolean) ? 'Desmarcar Todos' : 'Marcar Todos'}
                                            </Button>
                                        </div>

                                        <Form.Check
                                            type="checkbox"
                                            id="chk-A"
                                            className="mb-1 small fw-bold"
                                            label="Aberto (Ag. Lotes)"
                                            checked={statusTemp.A}
                                            onChange={() => handleStatusCheckboxChange('A')}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="chk-E"
                                            className="mb-1 small fw-bold"
                                            label="Em Coleta"
                                            checked={statusTemp.E}
                                            onChange={() => handleStatusCheckboxChange('E')}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="chk-C"
                                            className="mb-1 small fw-bold"
                                            label="Coletado"
                                            checked={statusTemp.C}
                                            onChange={() => handleStatusCheckboxChange('C')}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="chk-FERP"
                                            className="small fw-bold"
                                            label="Finalizado ERP"
                                            checked={statusTemp.FERP}
                                            onChange={() => handleStatusCheckboxChange('FERP')}
                                        />

                                        <div className="text-end mt-2 pt-1 border-top">
                                            <Button
                                                size="sm"
                                                className="btn-custom-red py-0 px-3 small fw-bold"
                                                onClick={() => {
                                                    setShowDropdownStatus(false);
                                                    setStatusAplicados(statusTemp);
                                                    buscarInventariosPorLoja(loja, statusTemp);
                                                }}
                                            >
                                                OK
                                            </Button>
                                        </div>
                                    </div>,
                                    document.body
                                )}
                            </div>

                            <div className="col-md-2">
                                <Form.Label className="small fw-bold mb-1">Tipo Inventário</Form.Label>
                                <Form.Select
                                    style={{ height: '38px' }}
                                    value={tipoFiltro}
                                    onChange={e => setTipoFiltro(e.target.value)}
                                >
                                    <option value="TODOS">(Todos os Tipos)</option>
                                    <option value="GERAL">Geral</option>
                                    <option value="PARCIAL">Parcial (Seção)</option>
                                    <option value="ITENS">Itens</option>
                                    <option value="SEM ESTOQUE">Sem Estoque</option>
                                </Form.Select>
                            </div>

                            <div className="col-md-auto">
                                <Button
                                    className="btn-custom-red fw-bold px-3 d-flex align-items-center justify-content-center"
                                    style={{ height: '38px' }}
                                    onClick={() => {
                                        setStatusAplicados(statusTemp);
                                        buscarInventariosPorLoja(loja, statusTemp);
                                    }}
                                >
                                    <FaSyncAlt className="me-1" /> Pesquisar
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card shadow-sm border-0">
                    <div className="card-header card-header-red fw-bold d-flex align-items-center gap-2 py-2">
                        <FaBoxes /> Inventários Consinco
                    </div>
                    <div className="table-responsive">
                        <Table hover striped className="mb-0 align-middle text-center small">
                            <thead className="table-secondary">
                                <tr>
                                    <th>Nº Inventário</th>
                                    <th>Data Geração</th>
                                    <th>Descrição</th>
                                    <th>Tipo / Seções / Itens</th>
                                    <th>Status / Congelado</th>
                                    <th style={{ width: '380px' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inventariosFiltrados.length > 0 ? (
                                    inventariosFiltrados.map((inv, idx) => {
                                        const isItens = String(inv.tipo).toUpperCase() === 'ITENS';
                                        const isSemEstoque = String(inv.tipo).toUpperCase() === 'SEM ESTOQUE';
                                        return (
                                            <tr key={idx}>
                                                <td className="fw-bold">{inv.numinv}</td>
                                                <td>{inv.data}</td>
                                                <td className="text-start">{inv.descricao}</td>

                                                <td>
                                                    <Button
                                                        size="sm"
                                                        variant={isItens || isSemEstoque ? "outline-danger" : "outline-secondary"}
                                                        className="py-0 px-2 fw-bold"
                                                        onClick={() => handleAbrirTipoModal(inv)}
                                                    >
                                                        <FaListAlt className="me-1" /> {inv.tipo}
                                                    </Button>
                                                </td>

                                                <td>
                                                    <div className="d-flex justify-content-center gap-1">
                                                        {inv.status_calculado === 'A' && (
                                                            <Badge bg="secondary">Ag. Cadastrar Lotes</Badge>
                                                        )}
                                                        {inv.status_calculado === 'C' && (
                                                            <Badge bg="info">COLETADO</Badge>
                                                        )}
                                                        {inv.status_calculado === 'E' && (
                                                            <Badge bg="warning" text="dark">Em Coleta</Badge>
                                                        )}
                                                        {inv.status_calculado === 'FERP' && (
                                                            <Badge bg="dark">FINALIZADO ERP</Badge>
                                                        )}

                                                        {inv.congelado === 'S' ? (
                                                            <Badge bg="success">Sim</Badge>
                                                        ) : (
                                                            <Badge bg="secondary">Não</Badge>
                                                        )}
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className="d-inline-flex justify-content-end align-items-center gap-2 w-100">
                                                        {inv.status_calculado !== 'FERP' ? (
                                                            <Button
                                                                size="sm"
                                                                variant="success"
                                                                className="py-1 px-2 fw-bold text-nowrap"
                                                                onClick={() => handleGerenciarLotes(inv)}
                                                            >
                                                                <FaPlay className="me-1" /> Lotes Coleta
                                                            </Button>
                                                        ) : (
                                                            <div style={{ width: '115px' }}></div>
                                                        )}

                                                        <Button
                                                            size="sm"
                                                            variant="warning"
                                                            className="py-1 px-2 fw-bold text-nowrap"
                                                            onClick={() => handleAbrirDivergencias(inv)}
                                                        >
                                                            <FaExclamationTriangle className="me-1" /> Divergências
                                                        </Button>

                                                        {inv.status_calculado === 'C' ? (
                                                            <Button
                                                                size="sm"
                                                                variant="outline-dark"
                                                                className="py-1 px-2 fw-bold text-nowrap"
                                                                onClick={() => handleExportarConsinco(inv)}
                                                            >
                                                                <FaFileDownload className="me-1" /> Exportar TXT
                                                            </Button>
                                                        ) : (
                                                            <div style={{ width: '110px' }}></div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-muted py-3">
                                            Nenhum inventário localizado para os filtros selecionados.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </main>

            {/* Modal "SEM ESTOQUE" e "ITENS" com Agrupamento por Seções (Otimizado sem scroll horizontal) */}
            <Modal show={showModalSemEstoque} onHide={() => setShowModalSemEstoque(false)} size="xl" centered backdrop="static">
                <Modal.Header closeButton className="card-header-red py-2">
                    <Modal.Title className="fs-6 fw-bold">Produtos do Inventário {selectedInv?.numinv} ({selectedInv?.tipo})</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap bg-light p-2 rounded border">
                        <div className="d-flex align-items-center gap-2">
                            <Form.Label className="small fw-bold mb-0 text-nowrap">Filtrar Seção:</Form.Label>
                            <Form.Select size="sm" style={{ width: '250px' }} value={secaoFiltroSelect} onChange={e => setSecaoFiltroSelect(e.target.value)}>
                                <option value="TODAS">(Todas as Seções)</option>
                                {secoesUnicasSemEstoque.map(sec => (
                                    <option key={sec} value={sec}>{sec}</option>
                                ))}
                            </Form.Select>
                        </div>

                        <div className="d-flex align-items-center gap-3">
                            <Form.Check
                                type="switch"
                                id="chk-exibir-estoque"
                                label="Exibir Estoque"
                                className="small fw-bold mb-0"
                                checked={exibirEstoque}
                                onChange={e => setExibirEstoque(e.target.checked)}
                            />

                            <Button size="sm" variant="dark" className="fw-bold" onClick={imprimirRelatorioSemEstoque}>
                                <FaPrint className="me-1" /> Imprimir Relação
                            </Button>
                        </div>
                    </div>

                    <div className="table-responsive" style={{ maxHeight: '60vh', overflowX: 'hidden' }}>
                        <Table size="sm" bordered hover className="small align-middle text-center mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                            <thead className="table-dark sticky-top">
                                <tr>
                                    <th style={{ width: '12%' }}>Cód.</th>
                                    <th style={{ width: exibirEstoque ? '68%' : '78%' }}>Descrição / Múltiplos EANs</th>
                                    <th style={{ width: '10%' }}>Emb.</th>
                                    {exibirEstoque && <th style={{ width: '10%' }}>Estoque</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {produtosAgrupadosPorSecao.length > 0 ? (
                                    produtosAgrupadosPorSecao.map((grupo, gIdx) => (
                                        <React.Fragment key={gIdx}>
                                            <tr className="table-danger text-dark fw-bold text-start">
                                                <td colSpan={exibirEstoque ? 4 : 3} className="bg-opacity-25 px-2 py-1 fs-6">
                                                    {grupo.cod_secao ? `${grupo.cod_secao} - ` : ''}{grupo.desc_secao}
                                                </td>
                                            </tr>
                                            {grupo.itens.map((p, idx) => (
                                                <tr key={idx}>
                                                    <td className="fw-bold">{p.prod || p.codigo}</td>
                                                    <td className="text-start text-break">
                                                        <div>{p.descricao}</div>
                                                        <small className="text-muted d-block fw-monospace">
                                                            EANs: {p.eans_adicionais || p.ean || ''}
                                                        </small>
                                                    </td>
                                                    <td>{p.qtde_embalagem || 1}</td>
                                                    {exibirEstoque && <td className="fw-bold">{p.estoque || 0}</td>}
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={exibirEstoque ? 4 : 3} className="text-muted py-3">Nenhum produto encontrado.</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Modal Lotes */}
            <Modal show={showModalLotes} onHide={() => setShowModalLotes(false)} size="lg" centered>
                <Modal.Header closeButton className="card-header-red py-2">
                    <Modal.Title className="fs-6 fw-bold">Lotes de Coleta - Inv. Nº {selectedInv?.numinv}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {resumoLotes.prontoParaDivergencia && (
                        <div className="alert alert-success d-flex align-items-center fw-bold py-2 px-3 mb-3">
                            <FaCheckCircle className="me-2 fs-5" /> Todos os lotes ativos foram coletados! A divergência já pode ser processada.
                        </div>
                    )}

                    <div className="d-flex align-items-center justify-content-between mb-3 bg-light p-2 rounded border">
                        <div className="small fw-bold">
                            Lotes Ativos: <Badge bg="dark">{resumoLotes.total}</Badge> | Coletados: <Badge bg="success">{resumoLotes.coletados}</Badge>
                        </div>
                        {!resumoLotes.prontoParaDivergencia && (
                            <div className="d-flex align-items-center gap-2">
                                <Form.Label className="small fw-bold mb-0 text-nowrap">Qtde de Lotes a Liberar:</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    style={{ maxWidth: '80px' }}
                                    value={qtdeLotesLiberar}
                                    onChange={e => setQtdeLotesLiberar(e.target.value)}
                                />
                                <Button size="sm" variant="success" className="fw-bold text-nowrap" onClick={handleLiberarLotesEmMassa}>
                                    Liberar Lotes
                                </Button>
                            </div>
                        )}
                    </div>

                    <Table size="sm" bordered hover className="text-center small">
                        <thead className="table-dark">
                            <tr>
                                <th>Lote</th>
                                <th>Contagem</th>
                                <th>Status</th>
                                <th>Itens Coletados</th>
                                <th>Data Início</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lotes.map((l, i) => (
                                <tr key={i} className={l.inv_status === 'I' ? 'table-secondary opacity-50' : ''}>
                                    <td className="fw-bold">{l.inv_lote}</td>
                                    <td><Badge bg="secondary">{l.inv_contagem}ª Contagem</Badge></td>
                                    <td>
                                        {l.inv_status === 'L' && <Badge bg="primary">Liberado</Badge>}
                                        {l.inv_status === 'R' && <Badge bg="warning">Recontagem</Badge>}
                                        {l.inv_status === 'F' && <Badge bg="success">Finalizado</Badge>}
                                        {l.inv_status === 'I' && <Badge bg="danger">Inativo</Badge>}
                                    </td>
                                    <td>{l.total_itens || 0}</td>
                                    <td>{l.inv_data}</td>
                                    <td>
                                        <Button
                                            size="sm"
                                            variant={l.inv_status === 'I' ? "outline-success" : "outline-danger"}
                                            className="py-0 px-2 me-1"
                                            onClick={() => handleInativarLote(l)}
                                        >
                                            <FaBan className="me-1" /> {l.inv_status === 'I' ? 'Ativar' : 'Inativar'}
                                        </Button>
                                        <Button size="sm" variant="outline-danger" className="py-0 px-2 me-1" onClick={() => handleSolicitarRecontagem(l)}>
                                            Recontar
                                        </Button>
                                        <Button size="sm" variant="outline-dark" className="py-0 px-2" onClick={() => handleImprimirLote(l)}>
                                            <FaPrint className="me-1" /> Relatório
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Modal.Body>
            </Modal>

            {/* Modal Crítica (Ajustado para caber na largura total sem scroll lateral) */}
            <Modal show={showModalCritica} onHide={() => setShowModalCritica(false)} size="xl" centered backdrop="static">
                <Modal.Header closeButton className="card-header-red py-2">
                    <Modal.Title className="fs-6 fw-bold"><FaExclamationTriangle className="me-1" /> Crítica do Inventário - Nº {selectedInv?.numinv}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-3">
                    <div className="row g-2 align-items-end mb-3">
                        <div className="col-md-2">
                            <Form.Label className="small fw-bold mb-1">Divergência Acima (R$)</Form.Label>
                            <Form.Control
                                size="sm"
                                type="text"
                                className="fw-bold text-danger"
                                value={valorCorte}
                                onChange={e => setValorCorte(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && carregarCriticaComFiltros()}
                            />
                        </div>
                        <div className="col-md-3">
                            <Form.Label className="small fw-bold mb-1">Buscar Produtos</Form.Label>
                            <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Código ou Descrição..."
                                value={buscaProduto}
                                onChange={e => setBuscaProduto(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && carregarCriticaComFiltros()}
                            />
                        </div>
                        <div className="col-md-3">
                            <Form.Label className="small fw-bold mb-1">Ordenar Por (Interno da Seção)</Form.Label>
                            <Form.Select size="sm" value={ordemCritica} onChange={e => setOrdemCritica(e.target.value)}>
                                <option value="VALOR_DESC">Maior Valor Divergência</option>
                                <option value="VALOR_ASC">Menor Valor Divergência</option>
                                <option value="CODIGO">Código do Produto</option>
                                <option value="DESCRICAO">Descrição do Produto</option>
                            </Form.Select>
                        </div>
                        <div className="col-md-auto">
                            <Button size="sm" className="btn-custom-red fw-bold" onClick={() => carregarCriticaComFiltros()}>
                                <FaSearch className="me-1" /> Filtrar
                            </Button>
                        </div>
                        <div className="col-md-auto ms-auto d-flex gap-2">
                            {selectedInv?.status_calculado !== 'FERP' && (
                                <Button size="sm" variant="success" className="fw-bold" onClick={() => handleExportarConsinco(selectedInv)}>
                                    <FaFileDownload className="me-1" /> Exportar TXT
                                </Button>
                            )}
                            <Button size="sm" variant="dark" className="fw-bold" onClick={imprimirRelatorioCritica}>
                                <FaPrint className="me-1" /> Imprimir Crítica
                            </Button>
                        </div>
                    </div>

                    <div className="table-responsive" style={{ maxHeight: '60vh', overflowX: 'hidden' }}>
                        <Table size="sm" bordered hover className="align-middle text-center mb-0" style={{ tableLayout: 'fixed', width: '100%', fontSize: '0.8rem' }}>
                            <thead className="table-dark sticky-top">
                                <tr>
                                    <th style={{ width: '9%', padding: '6px 2px' }}>Código</th>
                                    <th style={{ width: '37%', padding: '6px 2px' }}>Descrição / EANs</th>
                                    <th style={{ width: '5%', padding: '6px 2px' }}>Emb.</th>
                                    <th style={{ width: '9%', padding: '6px 2px' }}>Custo (R$)</th>
                                    <th style={{ width: '8%', padding: '6px 2px' }}>Congelado</th>
                                    <th style={{ width: '8%', padding: '6px 2px' }}>Coletado</th>
                                    <th style={{ width: '8%', padding: '6px 2px' }}>Div. (Qtd)</th>
                                    <th style={{ width: '10%', padding: '6px 2px' }}>Div. (R$)</th>
                                    {selectedInv?.status_calculado !== 'FERP' && <th style={{ width: '6%', padding: '6px 2px' }}>Ação</th>}
                                </tr>
                            </thead>

                            <tbody>
                                {criticaAgrupadaPorSecao.map((grupo, gIdx) => (
                                    <React.Fragment key={gIdx}>
                                        <tr className="table-danger text-dark fw-bold text-start">
                                            <td colSpan={selectedInv?.status_calculado !== 'FERP' ? 9 : 8} className="bg-opacity-25 px-2 py-1 fs-6">
                                                {grupo.cod_secao ? `${grupo.cod_secao} - ` : ''}{grupo.desc_secao}
                                            </td>
                                        </tr>
                                        {grupo.itens.map((item, i) => {
                                            const isForaInventario = item.fora_inventario === 'S' || item.qtd_congelada === 0;
                                            return (
                                                <tr key={i} className={isForaInventario ? "table-warning fw-bold text-danger" : ""}>
                                                    <td className="fw-bold p-1">
                                                        {item.codigo}
                                                        {isForaInventario && <Badge bg="danger" className="d-block mt-1" style={{ fontSize: '0.65rem' }}>Fora Inv.</Badge>}
                                                    </td>
                                                    <td className="text-start text-break p-1">
                                                        <div>{item.descricao}</div>
                                                        <small className="text-muted d-block fw-monospace" style={{ fontSize: '0.72rem' }}>
                                                            EAN: {item.ean || item.codigo}
                                                        </small>
                                                    </td>
                                                    <td className="p-1">{item.emb || 'UN'}</td>
                                                    <td className="p-1">{formatarMoeda(item.custo)}</td>
                                                    <td className="p-1">{item.qtd_congelada}</td>
                                                    <td className="fw-bold p-1">{item.qtd_contada}</td>
                                                    <td className={`p-1 ${isForaInventario ? "text-danger fw-bold" : (item.divergencia_qtd < 0 ? "text-danger" : "text-success")}`}>
                                                        {item.divergencia_qtd}
                                                    </td>
                                                    <td className={`p-1 ${isForaInventario ? "text-danger fw-bold" : (item.divergencia_valor < 0 ? "text-danger" : "text-success")}`}>
                                                        {formatarMoeda(item.divergencia_valor)}
                                                    </td>
                                                    {selectedInv?.status_calculado !== 'FERP' && (
                                                        <td className="p-1">
                                                            <Button
                                                                size="sm"
                                                                variant="outline-dark"
                                                                className="py-0 px-1 fw-bold text-nowrap"
                                                                style={{ fontSize: '0.7rem' }}
                                                                title="Recontar Lote 9999"
                                                                onClick={() => {
                                                                    setItemPosCritica(item);
                                                                    setNovaQtdFinal(item.qtd_contada || 0);
                                                                    setShowModalPosCritica(true);
                                                                }}
                                                            >
                                                                <FaEdit />
                                                            </Button>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Modal Recontagem Pós-Crítica */}
            <Modal show={showModalPosCritica} onHide={() => setShowModalPosCritica(false)} centered size="sm">
                <Modal.Header closeButton className="card-header-red py-2">
                    <Modal.Title className="fs-6 fw-bold">Recontagem Pós-Crítica (9999)</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-2 small">
                        <strong>Produto:</strong> {itemPosCritica?.descricao}<br />
                        <strong>Qtd Atual Contada:</strong> {itemPosCritica?.qtd_contada || 0}
                    </div>
                    <Form.Group>
                        <Form.Label className="small fw-bold">Quantidade Final Informada:</Form.Label>
                        <Form.Control
                            size="sm"
                            type="number"
                            value={novaQtdFinal}
                            onChange={e => setNovaQtdFinal(e.target.value)}
                        />
                    </Form.Group>
                    <div className="mt-3 text-end">
                        <Button size="sm" variant="success" className="fw-bold" onClick={handleSalvarAjustePosCritica}>
                            Salvar Ajuste
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Modal Relatório Lotes Coletados */}
            <Modal show={showModalRelLote} onHide={() => setShowModalRelLote(false)} size="lg" centered>
                <Modal.Header closeButton className="card-header-red py-2">
                    <Modal.Title className="fs-6 fw-bold"><FaPrint className="me-2" /> Relatório de Lotes Coletados</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {relatorioLoteDados && (
                        <div>
                            <div className="border-bottom pb-2 mb-3">
                                <h4 className="fw-bold text-center mb-1" style={{ color: '#991b1b' }}>Lotes Coletados</h4>
                                <div className="d-flex justify-content-between small text-muted">
                                    <span>Status: C - Coletado</span>
                                    <span>Inventário: {selectedInv?.numinv}</span>
                                    <span>Lote: {relatorioLoteDados.cabecalho?.inv_lote}</span>
                                    <span>Contagem: {relatorioLoteDados.cabecalho?.inv_contagem}</span>
                                </div>
                                <div className="small text-muted mt-1">
                                    <span>Usuário: {relatorioLoteDados.cabecalho?.nome_usuario_coleta || 'Coletor Mobile'}</span>
                                    <span className="ms-3">Data/Hora: {relatorioLoteDados.cabecalho?.inv_data}</span>
                                </div>
                            </div>

                            <Table size="sm" bordered hover className="small text-center">
                                <thead className="table-secondary">
                                    <tr>
                                        <th>Seq</th>
                                        <th>Cód. Interno</th>
                                        <th>EAN</th>
                                        <th>Descrição</th>
                                        <th>Coletado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {relatorioLoteDados.itens?.map((it, idx) => (
                                        <tr key={idx}>
                                            <td>{it.seq}</td>
                                            <td className="fw-bold">{it.codigo || it.cod_produto}</td>
                                            <td>{it.ean}</td>
                                            <td className="text-start">{it.descricao}</td>
                                            <td className="fw-bold">{it.coletado}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <div className="text-end">
                                <Button size="sm" variant="dark" onClick={() => imprimirRelatorioLote(relatorioLoteDados)}>
                                    <FaPrint className="me-1" /> Imprimir PDF
                                </Button>
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {/* Modal Seções */}
            <Modal show={showModalSecoes} onHide={() => setShowModalSecoes(false)} centered size="md">
                <Modal.Header closeButton className="card-header-red py-2">
                    <Modal.Title className="fs-6 fw-bold"><FaListAlt className="me-1" /> Seções do Inventário Nº {selectedInv?.numinv}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Table size="sm" bordered hover className="small text-center mb-0">
                        <thead className="table-dark">
                            <tr>
                                <th>Cód. Seção</th>
                                <th>Descrição da Seção</th>
                            </tr>
                        </thead>
                        <tbody>
                            {secoesDoInventario.length > 0 ? (
                                secoesDoInventario.map((s, i) => (
                                    <tr key={i}>
                                        <td className="fw-bold">{s.secao}</td>
                                        <td className="text-start">{s.descricao}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="2" className="text-muted">Cód. Seções: {selectedInv?.secao || 'Nenhuma seção especificada'}</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Modal.Body>
            </Modal>

            <Footer />

            <RelatorioCriticaPrint
                inventario={selectedInv}
                loja={loja}
                valorCorte={valorCorteNumerico}
                dadosCritica={critica}
            />
        </div>
    );
}